import { useContext, useState } from 'react';
import store from 'client/store/store';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { setTimedContent } from 'client/store/alertSlice/alertSlice';
import { clearCreatedEnquiry } from 'client/store/enquirySlice/enquirySlice';
import { clearCreatedEnquiryRequest } from 'client/store/enquiryRequestSlice/enquiryRequestSlice';
import { clearCreatedWorkpack } from 'client/store/workpackSlice/workpackSlice';

import useAuth from 'client/utils/auth/useAuth';
import { useSubmit } from 'react-router';

import { MapContext } from 'os-elements';

import ActionMenuItem from 'client/components/ui/ActionMenu/ActionMenuItem/ActionMenuItem';
import ActionMenu from 'client/components/ui/ActionMenu/ActionMenu';
import { BookMarkModal } from './BookmarkModal/BookMarkModal';

import Icon from '@mdi/react';
import { mdiBookmark } from '@mdi/js';

import { bookmarkTypes, mapModes } from 'client/utils/constants/appStates';
import useDeviceType from 'client/utils/helpers/DeviceType/useDeviceType';
import { DESKTOP } from 'client/utils/constants/GDS/js/devices';
import { permissions } from 'client/utils/constants/permissions';
import { CREATE_WORKPACK_PAGE, CREATE_ENQUIRY_PAGE } from 'client/utils/routes/routeConstants';
import { getGeoJsonGeometry } from 'client/utils/map/geometryUtils';
import { enquiryFeatureFlag } from 'client/utils/constants/frontEndConfig';
import ShareDialog from 'client/components/aria/Dialog/ShareDialog/ShareDialog';
import Button from 'client/components/aria/Button/Button';

import styles from './BookmarkButton.module.scss';
import { logWithPrefix } from 'client/utils/logging';
import Tooltip from 'client/components/aria/Tooltip/Tooltip';
import { useHover } from 'react-aria';
import { isValidAoi } from 'client/utils/helpers/Aoi';
import { LINE_STRING, POINT } from 'client/utils/constants/geometryType';

const log = logWithPrefix('mapShare');

const BookMarkButton = ({ mapMode = [], clearCreatedEnquiry, clearCreatedEnquiryRequest, clearCreatedWorkpack }) => {
    const auth = useAuth();
    const { permissions: userPermissions } = auth;
    const intl = useIntl();
    const { map } = useContext(MapContext);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const { hoverProps } = useHover({
        onHoverStart: () => setShowTooltip(true),
        onHoverEnd: () => setShowTooltip(false)
    });
    const [shareDialog, setShareDialog] = useState(false);
    const [bookmarkType, setBookmarkType] = useState(bookmarkTypes.REQUEST);
    const submit = useSubmit();

    // disable pdf for line tool strictly if there is a line or point aoi
    const aoi = useSelector(state => state.aoi?.source);
    const [feature] = aoi?.getFeatures() || [];
    const geometryType = feature?.getGeometry()?.getType();
    const isLinePdfDisabled = geometryType === LINE_STRING || geometryType === POINT;

    const isAoiMode = mapMode.includes(mapModes.AOI);
    const DEVICE_TYPE = useDeviceType();
    const showCreatePdfTooltip = isLinePdfDisabled && mapMode.length > 1 && showTooltip;

    // Check if AOI is valid
    const myCoverage = store.getState?.()?.coverageAssetOwners?.myCoverage;
    const aoiSource = useSelector(state => state.aoi?.source);
    const aoiIsValid = isValidAoi(aoiSource, myCoverage);
    const hasDrawnAOI = aoiSource?.getFeatures()?.length > 0;

    const isBookmarkDisabled = !aoiIsValid && Boolean(feature);
    const handleBookmarkLink = () => {
        isAoiMode ? setBookmarkType(bookmarkTypes.REQUEST) : setBookmarkType(bookmarkTypes.LOCATION);
        setShowSaveDialog(true);
    };

    const handleShareLink = () => {
        const logMessage = JSON.stringify({
            path: '/' + window.location.href.split('/').pop()
        });
        log(
            'User shared location',
            logMessage
        );
        setShareDialog(true);
    };

    const handleWorkPackLink = async () => {
        // Clear any previously created enquiry or workpack to prevent navigation issues
        clearCreatedEnquiryRequest();
        clearCreatedWorkpack();
        const geometryString = getGeoJsonGeometry(map, 'aoi');
        if (!geometryString) {
            await store.dispatch(setTimedContent(intl.formatMessage({ id: 'bookmark.i2d.drawAOI' })));
            return;
        }
        const formData = new FormData();
        formData.append('draft', 'true');
        formData.append('geometry', geometryString);
        // Route to new Work Pack flow
        submit(formData, {
            action: CREATE_WORKPACK_PAGE,
            method: 'put'
        });
    };

    const handleNewEnquiriesLink = async () => {
        clearCreatedEnquiryRequest();
        clearCreatedWorkpack();

        const geometryString = getGeoJsonGeometry(map, 'aoi');
        if (!geometryString) {
            await store.dispatch(setTimedContent(intl.formatMessage({ id: 'bookmark.button.makeEnquiry.title' })));
            return;
        }

        const formData = new FormData();
        formData.append('draft', 'true');
        formData.append('geometry', geometryString);

        submit(formData, {
            action: CREATE_ENQUIRY_PAGE,
            method: 'put'
        });
    };

    const DesktopButtons = () => {
        return (
            <>
                <Button
                    variant="info"
                    data-testid="mapToolbar.save.bookmark.button-d"
                    onPress={() => handleBookmarkLink()}
                    isDisabled={isBookmarkDisabled}
                    title={aoiIsValid ? '' : intl.formatMessage({ id: 'bookmark.button.bookmarksDisabled.title' })}
                >
                    {intl.formatMessage({ id: 'mapToolbar.save.bookmark' })}
                </Button>
                <Button
                    variant="info"
                    data-testid="mapToolBar.share.button-d"
                    onPress={() => handleShareLink()}
                >
                    {intl.formatMessage({ id: 'mapToolbar.share' })}
                </Button>
                {(userPermissions?.includes(permissions.I2D_CREATE) || userPermissions?.includes(permissions.EMERGENCY_I2D_CREATE)) &&
                <div
                    className={styles.tooltipWrapper}
                    data-testid="mapToolBar.create.pdf.buttonWrapper"
                    {...(isLinePdfDisabled ? hoverProps : {})}
                    onClick={isLinePdfDisabled ? () => setShowTooltip(true) : undefined}
                >
                    {showCreatePdfTooltip &&
                    <Tooltip
                        isOpen={true}
                        data-testid="createPdf.tooltip"
                        className={styles.tooltip}
                        contentClassName={styles.tooltipContent}
                    >
                        <FormattedMessage id='bookmark.button.createWorkPack.tooltip' />
                    </Tooltip>
                    }
                    <Button
                        variant="info"
                        data-testid="mapToolBar.create.workpack.new.button-d"
                        onPress={() => handleWorkPackLink()}
                        isDisabled={(!hasDrawnAOI || isLinePdfDisabled)}
                        title={!hasDrawnAOI ? intl.formatMessage({ id: 'bookmark.button.createWorkPack.title' }) : undefined}
                    >
                        { intl.formatMessage({ id: 'mapToolbar.create.pdf' }) }
                    </Button>
                </div>
                }
                {enquiryFeatureFlag && userPermissions?.includes(permissions.ENQUIRY_CREATE) &&
                <Button
                    variant="default"
                    data-testid="mapToolBar.new.enquiries.button-d"
                    onPress={() => handleNewEnquiriesLink()}
                    isDisabled={!hasDrawnAOI}
                    title={!hasDrawnAOI ? intl.formatMessage({ id: 'bookmark.button.makeEnquiry.title' }) : undefined}
                >
                    {intl.formatMessage({ id: 'bookmark.button.makeEnquiry' })}
                </Button>
                }
            </>
        );
    };

    const MobileButtons = () => {
        return (
            <>
                <ActionMenu
                    className={styles.menu}
                    aria-label={intl.formatMessage({ id: 'bookmark.button.menu.text' })}
                    label={intl.formatMessage({ id: 'bookmark.button.menu.text' })}
                    content={
                        <>
                            <Icon path={mdiBookmark} size={0.9}/>
                            { DEVICE_TYPE === DESKTOP ? <> &nbsp;{intl.formatMessage({ id: 'bookmark.button.menu.text' })}</> : null}
                        </>
                    }
                    anchor={{
                        vertical: top,
                        horizontal: DEVICE_TYPE === DESKTOP ? 'left' : 'right'
                    }}
                    data-testid="bookmark.button"
                >
                    <ActionMenuItem
                        data-testid="mapToolbar.save.bookmark.button-m"
                        onClick={() => handleBookmarkLink()}
                    >
                        {intl.formatMessage({ id: 'mapToolbar.save.bookmark' })}
                    </ActionMenuItem>
                    <ActionMenuItem
                        data-testid="mapToolbar.share.button-m"
                        onClick={() => handleShareLink()}
                    >
                        {intl.formatMessage({ id: 'mapToolbar.share' })}
                    </ActionMenuItem>
                    {(userPermissions?.includes(permissions.I2D_CREATE) || userPermissions?.includes(permissions.EMERGENCY_I2D_CREATE)) &&
                        <ActionMenuItem data-testid="mapToolbar.create.workpack.new.button-m"
                            onClick={() => handleWorkPackLink()}
                            disabled={!hasDrawnAOI || isLinePdfDisabled}
                        >
                            {intl.formatMessage({ id: 'mapToolbar.create.pdf' })}
                        </ActionMenuItem>
                    }
                    {enquiryFeatureFlag && userPermissions?.includes(permissions.ENQUIRY_CREATE) &&
                    <ActionMenuItem
                        data-testid="mapToolbar.new.enquiries.button-m"
                        onClick={() => handleNewEnquiriesLink()}
                        disabled={!hasDrawnAOI}
                    >
                        {intl.formatMessage({ id: 'bookmark.button.makeEnquiry' })}
                    </ActionMenuItem>
                    }
                </ActionMenu>
            </>
        );
    };

    return <>
        {DEVICE_TYPE === DESKTOP ? <DesktopButtons/> : <MobileButtons/>}
        <BookMarkModal
            showSaveDialog={showSaveDialog}
            bookmarkType={bookmarkType}
            setShowSaveDialog={setShowSaveDialog}
        />
        <ShareDialog
            data-testid={'location.share.dialog'}
            isOpen={shareDialog}
            setIsOpen={setShareDialog}
            // line deepcode ignore OR: not an open redirect
            url={window.location.href}
            restrictedLink
        />
    </>;
};

const mapStateToProps = state => {
    const { map } = state;
    return {
        mapMode: map?.mode || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        clearCreatedEnquiry: (payload) => dispatch(clearCreatedEnquiry(payload)),
        clearCreatedEnquiryRequest: (payload) => dispatch(clearCreatedEnquiryRequest(payload)),
        clearCreatedWorkpack: (payload) => dispatch(clearCreatedWorkpack(payload))
    };
};

// Export unwrapped component for testing
export { BookMarkButton };

export default connect(mapStateToProps, mapDispatchToProps)(BookMarkButton);
